import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, LayoutGrid, FileText, Upload, X, Loader2, Copy, Save, Calendar as CalendarIcon, Download, Wand2, Check } from "lucide-react";

const CARD_STYLES = "bg-white border border-stone-200 hover:border-stone-300 transition-colors";
const BTN_PRIMARY = "inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 disabled:opacity-50 text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold";
const BTN_ACCENT = "inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#C9654C] disabled:opacity-50 text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold";
const BTN_GHOST = "inline-flex items-center gap-2 text-stone-700 hover:text-stone-900 hover:bg-stone-100 px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold border border-stone-200";
const INPUT = "w-full border border-stone-300 focus:border-stone-900 focus:outline-none px-3 py-2.5 text-sm bg-white";
const LABEL = "block text-eyebrow text-stone-500 mb-2";

const PILARES = [
  { v: 1, label: "1 · Playa Secreta (misterio)" },
  { v: 2, label: "2 · Así se siente descansar" },
  { v: 3, label: "3 · Interiores calmos" },
  { v: 4, label: "4 · Comunidad Barra" },
  { v: 5, label: "5 · Prueba social" },
  { v: 6, label: "6 · Guía práctica" },
  { v: 7, label: "7 · Prueba de valor / reserva" },
];

const buildAssetUrl = (storagePath) => {
  if (!storagePath) return "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/files/${storagePath}`;
};

export default function AIStudio({ token }) {
  const [tool, setTool] = useState("recreate-image");
  const tools = [
    { id: "recreate-image", label: "Recrear imagen", icon: ImageIcon, desc: "Subo una foto y la recreo con estética Pura Vida" },
    { id: "recreate-carousel", label: "Recrear carrusel", icon: LayoutGrid, desc: "Idea + fotos opcionales → carrusel completo con caption" },
    { id: "caption-only", label: "Solo caption", icon: FileText, desc: "Describo la publicación y genero copy con 5 hashtags" },
  ];

  return (
    <div>
      {/* Header estudio */}
      <div className="mb-8">
        <p className="text-eyebrow text-[#E07A5F] mb-2">Herramientas IA</p>
        <h2 className="font-serif-display text-3xl md:text-4xl mb-2">Estudio de contenido</h2>
        <p className="text-sm text-stone-600 max-w-2xl">
          Generación asistida por IA con la voz y estética de Playa Secreta. Todos los captions incluyen máximo 5 hashtags (nueva política de Instagram).
        </p>
        <div className="mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 text-xs" data-testid="ai-autosave-banner">
          <Check className="w-3.5 h-3.5" />
          <span className="font-medium">Auto-guardado activo</span>
          <span className="text-emerald-700">·</span>
          <span>Cada resultado se guarda en la biblioteca con etiquetas <code className="bg-white/50 px-1 py-0.5 rounded font-mono text-[10px]">imagen-recreada</code>, <code className="bg-white/50 px-1 py-0.5 rounded font-mono text-[10px]">carrusel-ia</code> o <code className="bg-white/50 px-1 py-0.5 rounded font-mono text-[10px]">caption-ia</code></span>
        </div>
      </div>

      {/* Tool switcher */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {tools.map((t) => {
          const Icon = t.icon;
          const active = tool === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              data-testid={`ai-tool-${t.id}`}
              className={`text-left p-5 border-l-2 transition-all ${active ? "bg-stone-900 text-white border-[#E07A5F]" : "bg-white border-stone-200 hover:border-stone-400 text-stone-800"}`}
            >
              <Icon className={`w-5 h-5 mb-2 ${active ? "text-[#E07A5F]" : "text-stone-500"}`} />
              <p className="text-eyebrow font-semibold mb-1">{t.label}</p>
              <p className={`text-xs ${active ? "text-stone-300" : "text-stone-500"}`}>{t.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Tool panel */}
      {tool === "recreate-image" && <RecreateImageTool token={token} />}
      {tool === "recreate-carousel" && <RecreateCarouselTool token={token} />}
      {tool === "caption-only" && <CaptionOnlyTool token={token} />}
    </div>
  );
}

// =============================================================================
// RECREATE IMAGE
// =============================================================================
function RecreateImageTool({ token }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const onFilePick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(f.type.toLowerCase())) {
      toast.error("Formato no soportado. Usa JPG, PNG o WEBP.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Máximo 12MB");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
    setResult(null);
  };

  const generate = async () => {
    if (!file) return toast.error("Sube una imagen primero");
    setBusy(true);
    toast.info("Analizando y recreando… (30-60s)");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (instructions.trim()) fd.append("instructions", instructions.trim());
      const res = await api.post("/admin/ai/recreate-image", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success("Imagen recreada · guardada en biblioteca 🖼️");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al recrear imagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${CARD_STYLES} p-6 md:p-8`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input */}
        <div>
          <label className={LABEL}>1. Sube la imagen a recrear</label>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); onFilePick(e.dataTransfer.files?.[0]); }}
            className="border-2 border-dashed border-stone-300 hover:border-stone-500 cursor-pointer aspect-[4/5] flex items-center justify-center bg-stone-50 mb-4 relative overflow-hidden"
            data-testid="ai-recreate-image-dropzone"
          >
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-stone-500 p-6">
                <Upload className="w-8 h-8 mx-auto mb-3 text-stone-400" />
                <p className="text-sm font-semibold">Haz clic o arrastra una imagen</p>
                <p className="text-xs text-stone-400 mt-1">JPG · PNG · WEBP · máx 12MB</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => onFilePick(e.target.files?.[0])}
            className="hidden"
            data-testid="ai-recreate-image-input"
          />

          <label className={LABEL}>2. Instrucciones especiales (opcional)</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Ej: hazla en golden hour, quítale la sombrilla azul, agrégale una hamaca…"
            rows={3}
            className={INPUT}
            data-testid="ai-recreate-image-instructions"
          />

          <button
            onClick={generate}
            disabled={busy || !file}
            className={`${BTN_ACCENT} w-full mt-5 justify-center`}
            data-testid="ai-recreate-image-generate"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {busy ? "Recreando…" : "Recrear con estética Pura Vida"}
          </button>
        </div>

        {/* Output */}
        <div>
          <label className={LABEL}>Resultado</label>
          {!result ? (
            <div className="border border-stone-200 aspect-[4/5] flex items-center justify-center bg-stone-50 text-stone-400 text-sm text-center p-6">
              {busy ? (
                <div>
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
                  <p>Analizando imagen con Claude Vision…</p>
                  <p className="text-xs mt-1">Después: generando con Nano Banana…</p>
                </div>
              ) : (
                <p>La imagen recreada aparecerá aquí</p>
              )}
            </div>
          ) : (
            <ResultCardImage result={result} token={token} onReset={() => { setResult(null); setFile(null); setPreview(null); setInstructions(""); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function ResultCardImage({ result, token, onReset }) {
  const [pickIdx, setPickIdx] = useState(0);
  const chosen = (result.variants && result.variants[pickIdx]) || {
    caption_es: result.caption_es, caption_en: result.caption_en, hashtags: result.hashtags,
  };
  return (
    <div>
      <div className="border border-stone-200 aspect-[4/5] bg-stone-100 overflow-hidden">
        <img src={buildAssetUrl(result.storage_path)} alt="recreada" className="w-full h-full object-cover" data-testid="ai-recreate-image-result" />
      </div>
      <CaptionBlock
        variants={result.variants}
        caption_es={result.caption_es}
        caption_en={result.caption_en}
        hashtags={result.hashtags}
        onPick={(i) => setPickIdx(i)}
      />
      <ResultActions
        token={token}
        payload={{
          hook: (chosen.caption_es || "").split("\n")[0]?.slice(0, 100) || "Nueva imagen",
          caption_es: chosen.caption_es,
          caption_en: chosen.caption_en,
          hashtags: chosen.hashtags,
          storage_path: result.storage_path,
          content_type: result.content_type || "image/png",
          asset_type: "photo",
          ig_content_type: "post",
          library_asset_id: result.asset_id,
        }}
        downloadUrl={buildAssetUrl(result.storage_path)}
        downloadName={`vpv-recreada-${Date.now()}.${(result.content_type || "").includes("png") ? "png" : "jpg"}`}
        onReset={onReset}
      />
    </div>
  );
}

// =============================================================================
// RECREATE CAROUSEL
// =============================================================================
function RecreateCarouselTool({ token }) {
  const [idea, setIdea] = useState("");
  const [pilar, setPilar] = useState(2);
  const [style, setStyle] = useState("elegant");
  const [useAiImages, setUseAiImages] = useState(false);
  const [inspirationIds, setInspirationIds] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!idea.trim()) return toast.error("Describe la idea del carrusel");
    if (!useAiImages && inspirationIds.length === 0) {
      return toast.error("Activa 'Generar con IA' o adjunta fotos de la biblioteca");
    }
    setBusy(true);
    toast.info(useAiImages ? "Creando plan y generando imágenes con IA… (1-2 min)" : "Creando plan y componiendo slides… (30-60s)");
    try {
      const res = await api.post("/admin/ai/recreate-carousel", {
        idea: idea.trim(),
        inspiration_asset_ids: inspirationIds,
        use_ai_images: useAiImages,
        style,
        pilar,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      toast.success(`Carrusel generado · ${res.data.slides?.length || 0} slides guardados en biblioteca 🎨`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar carrusel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${CARD_STYLES} p-6 md:p-8`}>
      {!result ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <label className={LABEL}>1. Describe la idea del carrusel</label>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder='Ej: "6 detalles que solo ves si vienes: cangrejos, luna llena, tortugas, conchitas, playa sola, fogata"'
              rows={5}
              className={INPUT}
              data-testid="ai-carousel-idea"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <div>
                <label className={LABEL}>Pilar de marca</label>
                <select value={pilar} onChange={(e) => setPilar(Number(e.target.value))} className={INPUT} data-testid="ai-carousel-pilar">
                  {PILARES.map((p) => (
                    <option key={p.v} value={p.v}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Estilo tipográfico</label>
                <select value={style} onChange={(e) => setStyle(e.target.value)} className={INPUT} data-testid="ai-carousel-style">
                  <option value="elegant">Elegante (DM Serif Display)</option>
                  <option value="minimal">Minimal (Outfit asimétrico)</option>
                  <option value="classic">Clásico (Cormorant terracota)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <label className={LABEL}>2. Imágenes de fondo</label>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-stone-200 hover:border-stone-400">
                <input
                  type="checkbox"
                  checked={useAiImages}
                  onChange={(e) => setUseAiImages(e.target.checked)}
                  className="mt-0.5 accent-[#E07A5F]"
                  data-testid="ai-carousel-toggle-ai"
                />
                <span>
                  <span className="block text-eyebrow font-semibold text-stone-800">Generar imágenes con IA</span>
                  <span className="block text-xs text-stone-500 mt-0.5">Nano Banana crea los fondos desde 0. Más lento pero 100% automático.</span>
                </span>
              </label>

              <div className="p-3 border border-stone-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-eyebrow font-semibold text-stone-800">Biblioteca IG</span>
                  <button
                    onClick={() => setLibraryOpen(true)}
                    className="text-xs uppercase tracking-[0.14em] font-semibold text-[#E07A5F] hover:underline"
                    data-testid="ai-carousel-open-library"
                  >
                    {inspirationIds.length > 0 ? `${inspirationIds.length} seleccionadas` : "Seleccionar"}
                  </button>
                </div>
                <p className="text-xs text-stone-500">Usa fotos ya subidas. Rápido y sin costo LLM.</p>
              </div>
            </div>

            <button
              onClick={generate}
              disabled={busy}
              className={`${BTN_ACCENT} w-full mt-6 justify-center`}
              data-testid="ai-carousel-generate"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {busy ? "Generando…" : "Generar carrusel"}
            </button>
          </div>
        </div>
      ) : (
        <CarouselResultView result={result} token={token} idea={idea} onReset={() => { setResult(null); setInspirationIds([]); }} />
      )}

      {libraryOpen && (
        <LibraryPickerModal
          token={token}
          selected={inspirationIds}
          onClose={() => setLibraryOpen(false)}
          onConfirm={(ids) => { setInspirationIds(ids); setLibraryOpen(false); }}
        />
      )}
    </div>
  );
}

function CarouselResultView({ result, token, idea, onReset }) {
  const [pickIdx, setPickIdx] = useState(0);
  const slides = result.slides || [];
  const hook = result.hook || idea;
  const chosen = (result.variants && result.variants[pickIdx]) || {
    caption_es: result.caption_es, caption_en: result.caption_en, hashtags: result.hashtags,
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-eyebrow text-stone-500 mb-1">Carrusel generado · {slides.length} slides · estilo {result.style}</p>
          <h3 className="font-serif-display text-2xl">{hook}</h3>
        </div>
        <button onClick={onReset} className={BTN_GHOST} data-testid="ai-carousel-new">
          <Sparkles className="w-4 h-4" /> Nuevo
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
        {slides.map((s) => (
          <div key={s.index} className="relative aspect-[4/5] bg-stone-100 border border-stone-200 overflow-hidden group">
            <img src={buildAssetUrl(s.storage_path)} alt={`slide ${s.index}`} className="w-full h-full object-cover" />
            <div className="absolute top-1.5 left-1.5 bg-white/95 text-stone-900 text-[10px] font-bold px-1.5 py-0.5">{s.index}</div>
          </div>
        ))}
      </div>
      <CaptionBlock
        variants={result.variants}
        caption_es={result.caption_es}
        caption_en={result.caption_en}
        hashtags={result.hashtags}
        onPick={(i) => setPickIdx(i)}
      />
      <ResultActions
        token={token}
        payload={{
          hook,
          caption_es: chosen.caption_es,
          caption_en: chosen.caption_en,
          hashtags: chosen.hashtags,
          slide_paths: slides.map((s) => s.storage_path),
          ig_content_type: "carousel",
          library_asset_ids: result.library_asset_ids || [],
        }}
        downloadUrl={null}
        downloadName={null}
        slidesForZip={slides}
        onReset={onReset}
      />
    </div>
  );
}

// =============================================================================
// CAPTION ONLY
// =============================================================================
function CaptionOnlyTool({ token }) {
  const [description, setDescription] = useState("");
  const [pilar, setPilar] = useState(2);
  const [ctype, setCtype] = useState("post");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pickIdx, setPickIdx] = useState(0);

  const generate = async () => {
    if (!description.trim()) return toast.error("Describe la publicación");
    setBusy(true);
    try {
      const res = await api.post("/admin/ai/generate-caption-only", {
        description: description.trim(),
        pilar,
        ig_content_type: ctype,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      setPickIdx(0);
      toast.success("3 variantes guardadas en biblioteca ✍️");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar caption");
    } finally {
      setBusy(false);
    }
  };

  const chosen = result && (result.variants && result.variants[pickIdx]) || (result ? {
    caption_es: result.caption_es, caption_en: result.caption_en, hashtags: result.hashtags,
  } : {});

  return (
    <div className={`${CARD_STYLES} p-6 md:p-8`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <label className={LABEL}>Describe tu publicación</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Ej: "Reel de una persona sola caminando en la playa al atardecer, con una copa de vino en la mano, música lofi"'
            rows={7}
            className={INPUT}
            data-testid="ai-caption-description"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={LABEL}>Pilar</label>
              <select value={pilar} onChange={(e) => setPilar(Number(e.target.value))} className={INPUT} data-testid="ai-caption-pilar">
                {PILARES.map((p) => (<option key={p.v} value={p.v}>{p.label}</option>))}
              </select>
            </div>
            <div>
              <label className={LABEL}>Formato</label>
              <select value={ctype} onChange={(e) => setCtype(e.target.value)} className={INPUT} data-testid="ai-caption-ctype">
                <option value="post">Post</option>
                <option value="reel">Reel</option>
                <option value="carousel">Carrusel</option>
                <option value="story">Historia</option>
              </select>
            </div>
          </div>
          <button onClick={generate} disabled={busy} className={`${BTN_ACCENT} w-full mt-5 justify-center`} data-testid="ai-caption-generate">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {busy ? "Generando 3 variantes…" : "Generar 3 variantes de caption"}
          </button>
        </div>
        <div>
          <label className={LABEL}>Captions generados</label>
          {!result ? (
            <div className="border border-stone-200 min-h-[300px] flex items-center justify-center bg-stone-50 text-stone-400 text-sm p-6">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : "Las 3 variantes aparecerán aquí"}
            </div>
          ) : (
            <div>
              <CaptionBlock
                variants={result.variants}
                caption_es={result.caption_es}
                caption_en={result.caption_en}
                hashtags={result.hashtags}
                onPick={(i) => setPickIdx(i)}
              />
              <ResultActions
                token={token}
                payload={{
                  hook: description.slice(0, 100),
                  caption_es: chosen.caption_es,
                  caption_en: chosen.caption_en,
                  hashtags: chosen.hashtags,
                  ig_content_type: ctype,
                  pilar,
                }}
                downloadUrl={null}
                downloadName={null}
                onReset={() => { setResult(null); setDescription(""); }}
                hideDownload
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================
function CaptionBlock({ variants, caption_es, caption_en, hashtags, onPick }) {
  // Prepara las variantes finales: si `variants` viene, úsalas; si no, wrap el legacy en una única variante
  const vs = Array.isArray(variants) && variants.length > 0
    ? variants
    : (caption_es || caption_en)
      ? [{ tone: null, caption_es, caption_en, hashtags: hashtags || [] }]
      : [];
  const [selected, setSelected] = useState(0);
  const copy = (t) => {
    navigator.clipboard.writeText(t || "");
    toast.success("Copiado");
  };
  if (vs.length === 0) return null;

  return (
    <div className="mt-5">
      {vs.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="ai-variants-tabs">
          {vs.map((v, i) => {
            const active = selected === i;
            const label = v.tone ? capitalize(v.tone) : `Variante ${i + 1}`;
            return (
              <button
                key={i}
                onClick={() => { setSelected(i); if (onPick) onPick(i, v); }}
                className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold border transition-all ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
                data-testid={`ai-variant-tab-${i}`}
              >
                <span className={active ? "text-[#E07A5F]" : "text-stone-400"}>0{i + 1}</span>
                {label}
              </button>
            );
          })}
        </div>
      )}

      <VariantContent v={vs[selected]} copy={copy} />
    </div>
  );
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function VariantContent({ v, copy }) {
  if (!v) return null;
  return (
    <div className="space-y-4">
      {v.caption_es && (
        <div className="border border-stone-200 bg-stone-50/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-eyebrow text-stone-500">Caption · Español (con hashtags)</span>
            <button onClick={() => copy(v.caption_es)} className="text-xs text-stone-600 hover:text-[#E07A5F] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copiar</button>
          </div>
          <p className="text-sm whitespace-pre-wrap font-body text-stone-800" data-testid="ai-result-caption-es">{v.caption_es}</p>
        </div>
      )}
      {v.caption_en && (
        <div className="border border-stone-200 bg-stone-50/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-eyebrow text-stone-500">Caption · English (with hashtags)</span>
            <button onClick={() => copy(v.caption_en)} className="text-xs text-stone-600 hover:text-[#E07A5F] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          </div>
          <p className="text-sm whitespace-pre-wrap font-body text-stone-800" data-testid="ai-result-caption-en">{v.caption_en}</p>
        </div>
      )}
      {Array.isArray(v.hashtags) && v.hashtags.length > 0 && (
        <div className="border border-stone-200 bg-stone-50/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-eyebrow text-stone-500">Hashtags · {v.hashtags.length}/5</span>
            <button onClick={() => copy(v.hashtags.join(" "))} className="text-xs text-stone-600 hover:text-[#E07A5F] inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copiar solo hashtags</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="ai-result-hashtags">
            {v.hashtags.map((h, i) => (
              <span key={i} className="text-xs bg-white border border-stone-200 px-2 py-1 text-stone-700">{h}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultActions({ token, payload, downloadUrl, downloadName, slidesForZip, onReset, hideDownload }) {
  const [savingAsPost, setSavingAsPost] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");

  const saveAsPost = async (status = "idea", publishDate = null) => {
    setSavingAsPost(true);
    try {
      const body = { ...payload, status };
      if (publishDate) body.publish_date = publishDate;
      await api.post("/admin/ai/save-as-post", body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(publishDate ? "Post agendado en el calendario" : "Guardado como idea en Galería IG");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingAsPost(false);
      setShowSchedule(false);
    }
  };

  const downloadZip = async () => {
    if (!slidesForZip?.length) return;
    toast.info("Descargando slides…");
    for (const s of slidesForZip) {
      const link = document.createElement("a");
      link.href = buildAssetUrl(s.storage_path);
      link.download = `slide-${s.index}.jpg`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      await new Promise((r) => setTimeout(r, 250));
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-stone-200">
      <div className="flex flex-wrap gap-2">
        {!hideDownload && downloadUrl && (
          <a href={downloadUrl} download={downloadName || "vpv.jpg"} target="_blank" rel="noreferrer" className={BTN_GHOST} data-testid="ai-result-download">
            <Download className="w-4 h-4" /> Descargar
          </a>
        )}
        {!hideDownload && slidesForZip?.length > 0 && (
          <button onClick={downloadZip} className={BTN_GHOST} data-testid="ai-result-download-zip">
            <Download className="w-4 h-4" /> Descargar {slidesForZip.length} slides
          </button>
        )}
        <button onClick={() => saveAsPost("idea")} disabled={savingAsPost} className={BTN_PRIMARY} data-testid="ai-result-save-gallery">
          {savingAsPost ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar en galería
        </button>
        <button onClick={() => setShowSchedule((v) => !v)} className={BTN_ACCENT} data-testid="ai-result-schedule">
          <CalendarIcon className="w-4 h-4" /> Agendar en calendario
        </button>
        {onReset && (
          <button onClick={onReset} className={BTN_GHOST} data-testid="ai-result-reset">
            <X className="w-4 h-4" /> Descartar
          </button>
        )}
      </div>
      {showSchedule && (
        <div className="mt-4 p-4 bg-stone-50 border border-stone-200 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className={LABEL}>Fecha de publicación</label>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              className={INPUT}
              data-testid="ai-result-schedule-date"
            />
          </div>
          <button
            onClick={() => {
              if (!scheduleDate) return toast.error("Elige una fecha");
              saveAsPost("agendado", `${scheduleDate}T09:00:00Z`);
            }}
            disabled={savingAsPost}
            className={BTN_PRIMARY}
            data-testid="ai-result-schedule-confirm"
          >
            <Check className="w-4 h-4" /> Confirmar
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LIBRARY PICKER
// =============================================================================
function LibraryPickerModal({ token, selected, onClose, onConfirm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(new Set(selected || []));

  const load = async () => {
    try {
      const res = await api.get("/admin/ig-assets?asset_type=photo", { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Error cargando biblioteca");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="ai-library-modal">
      <div className="bg-white max-w-5xl w-full max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-stone-200 flex items-center justify-between">
          <div>
            <p className="text-eyebrow text-stone-500">Biblioteca IG · fotos</p>
            <h3 className="font-serif-display text-xl">Selecciona fotos de inspiración</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100" data-testid="ai-library-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No hay fotos en la biblioteca aún. Sube algunas primero desde &ldquo;Galería IG&rdquo;.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {items.map((it) => {
                const on = picked.has(it.id);
                return (
                  <button
                    key={it.id}
                    onClick={() => toggle(it.id)}
                    className={`relative aspect-square bg-stone-100 border-2 transition-all ${on ? "border-[#E07A5F] ring-2 ring-[#E07A5F]/30" : "border-transparent hover:border-stone-400"}`}
                    data-testid={`ai-library-item-${it.id}`}
                  >
                    <img src={buildAssetUrl(it.storage_path)} alt="" className="w-full h-full object-cover" />
                    {on && (
                      <div className="absolute top-1 right-1 bg-[#E07A5F] text-white p-1 rounded-full">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-stone-200 flex items-center justify-between">
          <span className="text-sm text-stone-600">{picked.size} seleccionadas</span>
          <div className="flex gap-2">
            <button onClick={onClose} className={BTN_GHOST}>Cancelar</button>
            <button onClick={() => onConfirm([...picked])} className={BTN_PRIMARY} data-testid="ai-library-confirm">Confirmar selección</button>
          </div>
        </div>
      </div>
    </div>
  );
}
